import { CartAddEvent } from '@theme/events';

/**
 * Custom element that powers the Pilumo "build your own kit" page.
 *
 * Customers pick quantities of any listed products via +/- steppers. Once
 * the combined quantity reaches the configured minimum, the summary shows
 * an estimated discounted total (the real discount is applied automatically
 * by a matching Shopify automatic discount when the items reach checkout —
 * this element never applies a discount itself, it only mirrors it for
 * display purposes).
 *
 * Products with more than one variant (color and/or size) render the same
 * color-swatch / size-pill picker used on the Catalogo product grid
 * (snippets/pilumo-grid-*.liquid): each option group is a set of radio
 * inputs with [data-kit-option], and the card carries the product's full
 * variant list as JSON on [data-variant-matrix]. Picking an option looks up
 * the matching variant in that matrix and swaps the card's active variant
 * id, price, image and stock state before the item is added to the cart.
 */
class PilumoKitBuilder extends HTMLElement {
  /** @type {Map<HTMLElement, number>} */
  #quantities = new Map();

  connectedCallback() {
    this.minQty = Number(this.dataset.minQty) || 3;
    this.discountPercent = Number(this.dataset.discountPercent) || 0;
    this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';

    this.items = Array.from(this.querySelectorAll('[data-kit-item]'));
    this.items.forEach((item) => this.#quantities.set(item, 0));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onOptionChange);

    const ctaButton = this.querySelector('[data-kit-add-to-cart]');
    ctaButton?.addEventListener('click', () => this.#addKitToCart());

    this.#updateUI();
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const incrementButton = target.closest('[data-kit-increment]');
    const decrementButton = target.closest('[data-kit-decrement]');

    if (incrementButton) {
      const item = incrementButton.closest('[data-kit-item]');
      if (item) this.#changeQuantity(item, 1);
    } else if (decrementButton) {
      const item = decrementButton.closest('[data-kit-item]');
      if (item) this.#changeQuantity(item, -1);
    }
  };

  /**
   * Handles a click on a color swatch or size pill (the same
   * [data-kit-option] radio-group pattern used on the Catalogo grid).
   * Reads every checked option across the card's option groups, looks up
   * the matching variant in the card's [data-variant-matrix] JSON, and
   * swaps the active variant id, price, image and stock state. Resets the
   * card's quantity to 0 if the newly matched variant is sold out.
   *
   * @param {Event} event
   */
  #onOptionChange = (event) => {
    const input = /** @type {HTMLElement} */ (event.target).closest('[data-kit-option]');
    if (!(input instanceof HTMLInputElement)) return;

    const item = input.closest('[data-kit-item]');
    if (!item) return;

    // Move the visual "selected" ring to the swatch/pill just picked —
    // identical behavior to the Catalogo grid's own option groups.
    const group = input.closest('[data-pilumo-option-group]');
    if (group) {
      group.querySelectorAll('label').forEach((label) => label.classList.remove('is-selected'));
      const ownLabel = input.closest('label');
      if (ownLabel) ownLabel.classList.add('is-selected');
    }

    let matrix;
    try {
      matrix = JSON.parse(item.dataset.variantMatrix || '[]');
    } catch (error) {
      return;
    }
    if (!Array.isArray(matrix) || matrix.length === 0) return;

    /** @type {Record<string, string>} */
    const selections = {};
    item.querySelectorAll('[data-kit-option]:checked').forEach((checkedInput) => {
      if (checkedInput instanceof HTMLInputElement) {
        selections[checkedInput.dataset.optionPosition] = checkedInput.value;
      }
    });

    const match = matrix.find((variant) =>
      Object.keys(selections).every((position) => variant[`option${position}`] === selections[position])
    );
    if (!match) return;

    const available = Boolean(match.available);

    item.dataset.variantId = String(match.id);
    item.dataset.price = String(match.price || 0);

    const priceEl = item.querySelector('[data-kit-price]');
    if (priceEl) priceEl.textContent = this.#formatMoney(item.dataset.price);

    const imageEl = item.querySelector('[data-kit-image]');
    if (imageEl instanceof HTMLImageElement && match.image) {
      imageEl.src = match.image;
    }

    const oosEl = item.querySelector('[data-kit-oos]');
    if (oosEl instanceof HTMLElement) {
      oosEl.hidden = available;
    }

    const incrementButton = item.querySelector('[data-kit-increment]');
    if (incrementButton instanceof HTMLButtonElement) {
      incrementButton.disabled = !available;
    }

    // If the newly selected variant is out of stock, drop any quantity
    // already picked for this card rather than checking out a sold-out item.
    if (!available && (this.#quantities.get(item) || 0) > 0) {
      this.#quantities.set(item, 0);

      const countEl = item.querySelector('[data-kit-count]');
      if (countEl) countEl.textContent = '0';
      item.dataset.selected = 'false';

      const decrementButton = item.querySelector('[data-kit-decrement]');
      if (decrementButton instanceof HTMLButtonElement) decrementButton.disabled = true;
    }

    this.#updateUI();
  };

  /**
   * @param {HTMLElement} item
   * @param {number} delta
   */
  #changeQuantity(item, delta) {
    const current = this.#quantities.get(item) || 0;
    const next = Math.max(0, current + delta);
    this.#quantities.set(item, next);

    const countEl = item.querySelector('[data-kit-count]');
    if (countEl) countEl.textContent = String(next);
    item.dataset.selected = next > 0 ? 'true' : 'false';

    const decrementButton = item.querySelector('[data-kit-decrement]');
    if (decrementButton instanceof HTMLButtonElement) {
      decrementButton.disabled = next === 0;
    }

    this.#updateUI();
  }

  #formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return `€${amount}`;
  }

  #updateUI() {
    let totalQty = 0;
    let subtotalCents = 0;

    this.items.forEach((item) => {
      const qty = this.#quantities.get(item) || 0;
      const priceCents = Number(item.dataset.price) || 0;
      totalQty += qty;
      subtotalCents += qty * priceCents;
    });

    const progressFill = this.querySelector('[data-kit-progress-fill]');
    const progressLabel = this.querySelector('[data-kit-progress-label]');
    const note = this.querySelector('[data-kit-note]');
    const originalEl = this.querySelector('[data-kit-original]');
    const totalEl = this.querySelector('[data-kit-total]');
    const savingsEl = this.querySelector('[data-kit-savings]');
    const ctaButton = this.querySelector('[data-kit-add-to-cart]');

    const unlocked = totalQty >= this.minQty;
    const percentForDisplay = Math.min(100, Math.round((totalQty / this.minQty) * 100));

    if (progressFill instanceof HTMLElement) {
      progressFill.style.width = `${percentForDisplay}%`;
    }

    if (progressLabel) {
      const shownCount = Math.min(totalQty, this.minQty);
      progressLabel.textContent = `${shownCount} / ${this.minQty} prodotti selezionati`;
    }

    // Savings amount is shown both in the note text and as a standalone chip
    // directly next to the price — surfacing it right next to the price,
    // rather than only lower on the page, is the change most consistently
    // tied to a conversion lift for this page type.
    const savingsCents = unlocked ? Math.round(subtotalCents * (this.discountPercent / 100)) : 0;

    if (note) {
      if (unlocked) {
        note.textContent =
          this.discountPercent > 0
            ? `✓ Sconto del ${this.discountPercent}% sbloccato — risparmi ${this.#formatMoney(savingsCents)}!`
            : `✓ Sconto sbloccato!`;
        note.setAttribute('data-unlocked', 'true');
      } else {
        const missing = this.minQty - totalQty;
        note.textContent =
          missing === 1
            ? `Aggiungi ancora 1 prodotto per sbloccare il ${this.discountPercent}% di sconto`
            : `Aggiungi almeno ${missing} prodotti per sbloccare il ${this.discountPercent}% di sconto`;
        note.setAttribute('data-unlocked', 'false');
      }
    }

    if (unlocked && this.discountPercent > 0) {
      const discountedCents = subtotalCents - savingsCents;
      if (originalEl) {
        originalEl.textContent = this.#formatMoney(subtotalCents);
        originalEl.hidden = false;
      }
      if (totalEl) totalEl.textContent = this.#formatMoney(discountedCents);
      if (savingsEl) {
        savingsEl.textContent = `Risparmi ${this.#formatMoney(savingsCents)}`;
        savingsEl.hidden = false;
      }
    } else {
      if (originalEl) originalEl.hidden = true;
      if (totalEl) totalEl.textContent = this.#formatMoney(subtotalCents);
      if (savingsEl) savingsEl.hidden = true;
    }

    if (ctaButton instanceof HTMLButtonElement) {
      ctaButton.disabled = totalQty === 0;
    }
  }

  async #addKitToCart() {
    const ctaButton = this.querySelector('[data-kit-add-to-cart]');
    const items = this.items
      .map((item) => ({
        id: Number(item.dataset.variantId),
        quantity: this.#quantities.get(item) || 0,
      }))
      .filter((line) => line.quantity > 0 && line.id);

    if (items.length === 0) return;

    if (ctaButton instanceof HTMLButtonElement) {
      ctaButton.disabled = true;
      var originalLabel = ctaButton.textContent;
      ctaButton.textContent = 'Aggiunta in corso...';
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.push(el.dataset.sectionId);
      }
    });

    try {
      const response = await fetch(this.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });

      const data = await response.json();

      if (data.status) {
        if (ctaButton instanceof HTMLButtonElement) {
          ctaButton.disabled = false;
          ctaButton.textContent = originalLabel;
        }
        console.error('Pilumo kit builder: add to cart failed', data);
        return;
      }

      const cart = await fetch('/cart.js').then((res) => res.json());

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'pilumo-kit-builder', {
          source: 'pilumo-kit-builder',
          itemCount: cart.item_count,
          sections: data.sections,
        })
      );

      if (ctaButton instanceof HTMLButtonElement) {
        ctaButton.disabled = false;
        ctaButton.textContent = 'Aggiunto! ✓';
        setTimeout(() => {
          ctaButton.textContent = originalLabel;
        }, 2000);
      }
    } catch (error) {
      console.error('Pilumo kit builder: add to cart error', error);
      if (ctaButton instanceof HTMLButtonElement) {
        ctaButton.disabled = false;
        ctaButton.textContent = originalLabel;
      }
    }
  }
}

if (!customElements.get('pilumo-kit-builder')) {
  customElements.define('pilumo-kit-builder', PilumoKitBuilder);
}
